import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { api, type User } from './api';
import { clearAnalyticsUserId, initAnalytics, setAnalyticsUserId, trackPageView } from './analytics';
import { ConfirmLogin } from './screens/ConfirmLogin';
import { Landing } from './screens/Landing';
import { Login } from './screens/Login';
import { Profile } from './screens/Profile';

// Staff-only and by far the largest screen (CRUD forms, QR scanner) — split
// out of the main bundle so the public landing page does not pay to download
// and parse it.
const Admin = lazy(() => import('./screens/Admin').then((m) => ({ default: m.Admin })));

/**
 * Screens live at real paths because the OAuth callback redirects the browser
 * back to one of them; a purely in-memory screen switch could not be a
 * redirect target.
 */
function usePath(): [string, (next: string) => void] {
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((next: string) => {
    if (next === window.location.pathname) return;
    window.history.pushState({}, '', next);
    setPath(next);
    window.scrollTo(0, 0);
  }, []);

  return [path, navigate];
}

export function App() {
  const [path, navigate] = usePath();
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.me();
      setUser(res.user);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    void refreshUser().finally(() => setReady(true));
  }, [refreshUser]);

  useEffect(() => {
    initAnalytics();
  }, []);

  // Fires on the first render too, so the landing pageview is never missed.
  useEffect(() => {
    trackPageView(path);
  }, [path]);

  // Ties tracking to the internal customer id (never the email) once signed
  // in, so behaviour can be followed across devices without exporting PII.
  useEffect(() => {
    if (user) setAnalyticsUserId(String(user.id));
    else clearAnalyticsUserId();
  }, [user?.id]);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
      navigate('/');
    }
  }, [navigate]);

  if (!ready) {
    return (
      <div className="app loading-screen">
        <span className="spinner" />
      </div>
    );
  }

  // Guards: signed-out visitors get the login screen, and the panel is staff
  // only. The server enforces the same rules — this is just the friendly path.
  if (path === '/perfil' && !user) {
    return (
      <div className="app">
        <Login navigate={navigate} onSignedIn={refreshUser} />
      </div>
    );
  }

  if (path === '/admin' && (!user || user.role !== 'staff')) {
    return (
      <div className="app">
        {user ? (
          <Profile
            user={user}
            navigate={navigate}
            onLogout={logout}
            refreshUser={refreshUser}
          />
        ) : (
          <Login navigate={navigate} onSignedIn={refreshUser} />
        )}
      </div>
    );
  }

  // The emailed link lands here, whether or not a session already exists.
  if (path === '/entrar') {
    return (
      <div className="app">
        <ConfirmLogin navigate={navigate} onSignedIn={refreshUser} />
      </div>
    );
  }

  return (
    <div className="app">
      {path === '/login' && <Login navigate={navigate} onSignedIn={refreshUser} />}

      {path === '/perfil' && user && (
        <Profile user={user} navigate={navigate} onLogout={logout} refreshUser={refreshUser} />
      )}

      {path === '/admin' && user?.role === 'staff' && (
        <Suspense fallback={<span className="spinner" />}>
          <Admin navigate={navigate} onLogout={logout} />
        </Suspense>
      )}

      {path !== '/login' && path !== '/perfil' && path !== '/admin' && path !== '/entrar' && (
        <Landing user={user} navigate={navigate} />
      )}
    </div>
  );
}
